import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getAdminStats, getApplications, getAllUsers, getPrograms, getUniversities,
  getAdminFinancialReport, createProgram, createUniversity, updateProgram,
  deleteProgram, updateUniversity, deleteUniversity, updateApplicationStatus,
  runScraper, getScrapeLogs, getDisbursements, getDisbursementSummary, payDisbursement
} from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import { EduProAdminPanel } from '../components/EduProAdminPanel';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, FileText, TrendingUp, DollarSign, LogOut, Building2, BookOpen,
  CheckCircle, Clock, XCircle, Plus, Edit, Trash2, RefreshCw, Download,
  AlertCircle, CreditCard, Search as SearchIcon, Loader2
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [disbursements, setDisbursements] = useState([]);
  const [disbursementSummary, setDisbursementSummary] = useState(null);
  const [scrapeLogs, setScrapeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showUniModal, setShowUniModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingUni, setEditingUni] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [savingProgram, setSavingProgram] = useState(false);
  const [savingUni, setSavingUni] = useState(false);

  // Form states
  const [programForm, setProgramForm] = useState({ name: '', category: '', description: '', duration_months: '', price: '', modality: 'online', university_id: '' });
  const [uniForm, setUniForm] = useState({ name: '', slug: '', description: '', website: '', logo_url: '' });
  const [statusForm, setStatusForm] = useState({ status: '', reason: '' });

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = useCallback(async () => {
    try {
      const [statsRes, appsRes, usersRes, progsRes, uniRes] = await Promise.all([
        getAdminStats(), getApplications(), getAllUsers(), getPrograms({ limit: 100 }), getUniversities()
      ]);
      setStats(statsRes.data);
      setApplications(appsRes.data || []);
      setUsers(usersRes.data || []);
      setPrograms(progsRes.data.programs || []);
      setUniversities(uniRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const fetchDisbursements = async () => {
    try {
      const [disbRes, summRes] = await Promise.all([getDisbursements(), getDisbursementSummary()]);
      setDisbursements(disbRes.data || []);
      setDisbursementSummary(summRes.data);
    } catch (e) { console.error(e); }
  };

  const fetchScrapeLogs = async () => {
    try {
      const res = await getScrapeLogs();
      setScrapeLogs(res.data || []);
    } catch (e) { console.error(e); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'disbursements') fetchDisbursements();
    if (tab === 'scraper') fetchScrapeLogs();
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(p || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const getStatusBadge = (status) => {
    const cfg = {
      draft: { label: 'Borrador', cls: 'bg-slate-100 text-slate-700' },
      submitted: { label: 'Enviada', cls: 'bg-blue-100 text-blue-700' },
      under_review: { label: 'En revision', cls: 'bg-amber-100 text-amber-700' },
      approved: { label: 'Aprobada', cls: 'bg-green-100 text-green-700' },
      rejected: { label: 'Rechazada', cls: 'bg-red-100 text-red-700' },
      disbursed: { label: 'Desembolsado', cls: 'bg-purple-100 text-purple-700' },
      documents_required: { label: 'Docs pendientes', cls: 'bg-orange-100 text-orange-700' },
    };
    const c = cfg[status] || { label: status, cls: 'bg-slate-100 text-slate-700' };
    return <Badge className={c.cls}>{c.label}</Badge>;
  };

  // Program CRUD
  const openProgramModal = (prog = null) => {
    if (prog) {
      setEditingProgram(prog);
      setProgramForm({ name: prog.name, category: prog.category || '', description: prog.description || '', duration_months: String(prog.duration_months || ''), price: String(prog.price || ''), modality: prog.modality || 'online', university_id: prog.university_id });
    } else {
      setEditingProgram(null);
      setProgramForm({ name: '', category: '', description: '', duration_months: '', price: '', modality: 'online', university_id: universities[0]?.university_id || '' });
    }
    setShowProgramModal(true);
  };

  const saveProgram = async () => {
    setSavingProgram(true);
    try {
      const payload = { ...programForm, duration_months: parseInt(programForm.duration_months), price: parseFloat(programForm.price) };
      if (editingProgram) {
        await updateProgram(editingProgram.program_id, payload);
      } else {
        await createProgram(payload);
      }
      setShowProgramModal(false);
      fetchAllData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSavingProgram(false);
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm('Eliminar este programa?')) return;
    try { await deleteProgram(id); fetchAllData(); } catch (e) { alert('Error al eliminar'); }
  };

  // University CRUD
  const openUniModal = (uni = null) => {
    if (uni) {
      setEditingUni(uni);
      setUniForm({ name: uni.name, slug: uni.slug || '', description: uni.description || '', website: uni.website || '', logo_url: uni.logo_url || '' });
    } else {
      setEditingUni(null);
      setUniForm({ name: '', slug: '', description: '', website: '', logo_url: '' });
    }
    setShowUniModal(true);
  };

  const saveUniversity = async () => {
    setSavingUni(true);
    try {
      if (editingUni) {
        await updateUniversity(editingUni.university_id, uniForm);
      } else {
        await createUniversity(uniForm);
      }
      setShowUniModal(false);
      fetchAllData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSavingUni(false);
  };

  const handleDeleteUni = async (id) => {
    if (!window.confirm('Eliminar universidad y todos sus programas?')) return;
    try { await deleteUniversity(id); fetchAllData(); } catch (e) { alert('Error al eliminar'); }
  };

  // Application status
  const openStatusModal = (app) => {
    setSelectedApp(app);
    setStatusForm({ status: app.status, reason: '' });
    setShowStatusModal(true);
  };

  const saveStatus = async () => {
    try {
      await updateApplicationStatus(selectedApp.application_id, statusForm);
      setShowStatusModal(false);
      fetchAllData();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  // Scraper
  const handleRunScraper = async (universityId = null) => {
    setScraping(true);
    try {
      const res = await runScraper(universityId);
      alert(`Scraping completado: ${JSON.stringify(res.data.message || 'OK')}`);
      fetchAllData();
      fetchScrapeLogs();
    } catch (e) { alert('Error en scraping: ' + (e.response?.data?.detail || e.message)); }
    setScraping(false);
  };

  // Disbursement
  const handlePayDisbursement = async (id) => {
    if (!window.confirm('Confirmar pago a universidad?')) return;
    try {
      await payDisbursement(id);
      fetchDisbursements();
    } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-brand-blue text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <EdFiLogo size="sm" linkTo={null} className="brightness-0 invert" />
              <Badge className="bg-white/20 text-white">Admin</Badge>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/analytics">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" data-testid="analytics-link">
                <TrendingUp className="w-4 h-4 mr-1" /> Analitica
              </Button>
            </Link>
            <span className="text-white/80 hidden md:block">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="admin-logout">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-8" data-testid="admin-dashboard-title">Panel Administrativo</h1>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold" data-testid="admin-users-count">{stats.total_users}</p><p className="text-xs text-slate-600">Estudiantes</p></div></div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center"><BookOpen className="w-5 h-5 text-cyan-600" /></div><div><p className="text-2xl font-bold" data-testid="admin-programs-count">{stats.total_programs}</p><p className="text-xs text-slate-600">Programas</p></div></div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600" /></div><div><p className="text-2xl font-bold text-green-600">{stats.approved_applications}</p><p className="text-xs text-slate-600">Aprobadas</p></div></div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><DollarSign className="w-5 h-5 text-purple-600" /></div><div><p className="text-xl font-bold text-purple-600">{formatPrice(stats.total_approved_amount)}</p><p className="text-xs text-slate-600">Monto Aprobado</p></div></div></CardContent></Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="overview" data-testid="tab-overview">Solicitudes</TabsTrigger>
            <TabsTrigger value="edupro" data-testid="tab-edupro">EduPro</TabsTrigger>
            <TabsTrigger value="programs" data-testid="tab-programs">Programas</TabsTrigger>
            <TabsTrigger value="universities" data-testid="tab-universities">Universidades</TabsTrigger>
            <TabsTrigger value="disbursements" data-testid="tab-disbursements">Desembolsos</TabsTrigger>
            <TabsTrigger value="scraper" data-testid="tab-scraper">Scraper</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader><CardTitle>Solicitudes de Credito</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>ID</TableHead><TableHead>Alumno</TableHead><TableHead>Programa</TableHead>
                    <TableHead className="text-right">Monto</TableHead><TableHead>Estado</TableHead>
                    <TableHead>Fecha</TableHead><TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {applications.map(app => (
                      <TableRow key={app.application_id}>
                        <TableCell className="font-mono text-xs">{app.application_id?.slice(0, 12)}</TableCell>
                        <TableCell>{app.user_email || app.user_id?.slice(0, 10)}</TableCell>
                        <TableCell>{app.program_name || app.program_id}</TableCell>
                        <TableCell className="text-right">{formatPrice(app.amount_requested || app.requested_amount)}</TableCell>
                        <TableCell>{getStatusBadge(app.status)}</TableCell>
                        <TableCell>{formatDate(app.created_at)}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => openStatusModal(app)} data-testid={`edit-app-status-${app.application_id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {applications.length === 0 && <p className="text-center text-slate-500 py-8">No hay solicitudes</p>}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EduPro Tab */}
          <TabsContent value="edupro">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2">Solicitudes EduPro <img src="/edupro-logo.png" alt="EduPro" className="h-5 w-auto" /></CardTitle></CardHeader>
              <CardContent>
                <EduProAdminPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle data-testid="admin-programs-title">Catalogo de Programas ({programs.length})</CardTitle>
                <Button onClick={() => openProgramModal()} className="bg-brand-blue" data-testid="add-program-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nuevo Programa
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Nombre</TableHead><TableHead>Universidad</TableHead><TableHead>Categoria</TableHead>
                    <TableHead>Modalidad</TableHead><TableHead>Duracion</TableHead>
                    <TableHead className="text-right">Precio</TableHead><TableHead className="text-right">Ed.Fi paga (80%)</TableHead>
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {programs.map(p => (
                      <TableRow key={p.program_id}>
                        <TableCell className="font-medium max-w-[200px] truncate">{p.name}</TableCell>
                        <TableCell>{p.university_name || p.university_id}</TableCell>
                        <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                        <TableCell>{p.modality}</TableCell>
                        <TableCell>{p.duration_months}m</TableCell>
                        <TableCell className="text-right font-semibold">{formatPrice(p.price)}</TableCell>
                        <TableCell className="text-right text-brand-blue">{formatPrice(p.price * 0.8)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openProgramModal(p)} data-testid={`edit-prog-${p.program_id}`}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteProgram(p.program_id)} className="text-red-500 hover:text-red-700" data-testid={`delete-prog-${p.program_id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Universities Tab */}
          <TabsContent value="universities">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Universidades ({universities.length})</CardTitle>
                <Button onClick={() => openUniModal()} className="bg-brand-blue" data-testid="add-university-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nueva Universidad
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {universities.map(u => (
                    <div key={u.university_id} className="border rounded-xl p-6" data-testid={`uni-card-${u.university_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-brand-blue rounded-lg flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{u.name}</h3>
                            <p className="text-sm text-slate-500">{u.website}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openUniModal(u)} data-testid={`edit-uni-${u.university_id}`}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteUni(u.university_id)} className="text-red-500" data-testid={`delete-uni-${u.university_id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 mb-3">{u.description?.slice(0, 120)}</p>
                      <div className="flex gap-2">
                        <Badge>{programs.filter(p => p.university_id === u.university_id).length} programas</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disbursements Tab */}
          <TabsContent value="disbursements">
            <div className="space-y-6">
              {disbursementSummary && (
                <div className="grid md:grid-cols-4 gap-4">
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Pendientes</p><p className="text-2xl font-bold text-amber-600">{disbursementSummary.summary?.pending?.count || 0}</p><p className="text-sm text-slate-500">{formatPrice(disbursementSummary.summary?.pending?.total || 0)}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Pagados</p><p className="text-2xl font-bold text-green-600">{disbursementSummary.summary?.paid?.count || 0}</p><p className="text-sm text-slate-500">{formatPrice(disbursementSummary.summary?.paid?.total || 0)}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Margen Ed.Fi (Pendiente)</p><p className="text-2xl font-bold text-brand-blue">{formatPrice(disbursementSummary.summary?.pending?.margin || 0)}</p></CardContent></Card>
                  <Card><CardContent className="pt-6"><p className="text-sm text-slate-600">Margen Ed.Fi (Pagado)</p><p className="text-2xl font-bold text-purple-600">{formatPrice(disbursementSummary.summary?.paid?.margin || 0)}</p></CardContent></Card>
                </div>
              )}
              <Card>
                <CardHeader><CardTitle>Desembolsos a Universidades</CardTitle></CardHeader>
                <CardContent>
                  {disbursements.length === 0 ? (
                    <div className="text-center py-12"><CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">No hay desembolsos pendientes. Se crean automaticamente al firmar un contrato.</p></div>
                  ) : (
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Universidad</TableHead><TableHead>Programa</TableHead>
                        <TableHead className="text-right">Precio Programa</TableHead>
                        <TableHead className="text-right">Pago Universidad (80%)</TableHead>
                        <TableHead className="text-right">Margen Ed.Fi (20%)</TableHead>
                        <TableHead>Estado</TableHead><TableHead>Referencia</TableHead><TableHead></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {disbursements.map(d => (
                          <TableRow key={d.disbursement_id}>
                            <TableCell className="font-medium">{d.university_name}</TableCell>
                            <TableCell>{d.program_name}</TableCell>
                            <TableCell className="text-right">{formatPrice(d.program_price)}</TableCell>
                            <TableCell className="text-right font-semibold text-green-700">{formatPrice(d.university_amount)}</TableCell>
                            <TableCell className="text-right font-semibold text-brand-blue">{formatPrice(d.edfi_margin)}</TableCell>
                            <TableCell>
                              <Badge className={d.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                {d.status === 'paid' ? 'Pagado' : 'Pendiente'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{d.payment_reference || '-'}</TableCell>
                            <TableCell>
                              {d.status === 'pending' && (
                                <Button size="sm" onClick={() => handlePayDisbursement(d.disbursement_id)} className="bg-green-600 hover:bg-green-700" data-testid={`pay-disb-${d.disbursement_id}`}>
                                  <DollarSign className="w-4 h-4 mr-1" /> Pagar
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Scraper Tab */}
          <TabsContent value="scraper">
            <div className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Web Scraper de Universidades</CardTitle>
                    <p className="text-sm text-slate-600 mt-1">Busca y actualiza automaticamente programas desde las paginas web de universidades</p>
                  </div>
                  <div className="flex gap-2">
                    {universities.map(u => (
                      <Button key={u.university_id} variant="outline" onClick={() => handleRunScraper(u.university_id)} disabled={scraping} data-testid={`scrape-${u.university_id}`}>
                        {scraping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        {u.name}
                      </Button>
                    ))}
                    <Button onClick={() => handleRunScraper()} disabled={scraping} className="bg-brand-blue" data-testid="scrape-all-btn">
                      {scraping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Scrape Todos
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium mb-2">Fuentes configuradas:</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2"><Building2 className="w-4 h-4 text-brand-blue" /><span className="font-medium">Anahuac Online:</span> <a href="https://online.anahuac.mx/collections/maestrias" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">online.anahuac.mx</a></li>
                      <li className="flex items-center gap-2"><Building2 className="w-4 h-4 text-brand-blue" /><span className="font-medium">IADA:</span> <a href="https://iada.anahuac.mx/collections/programas" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">iada.anahuac.mx</a></li>
                    </ul>
                  </div>
                  {scrapeLogs.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">Historial de Scraping</h4>
                      {scrapeLogs.map((log, i) => (
                        <div key={i} className="border rounded-lg p-4 mb-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-slate-500">{formatDate(log.scraped_at)}</span>
                          </div>
                          {log.results && Object.entries(log.results).map(([uid, res]) => (
                            <div key={uid} className="text-sm">
                              <span className="font-medium">{res.university_name || uid}:</span>{' '}
                              {res.synced ? (
                                <span className="text-green-600">{res.new_programs} nuevos, {res.updated_programs} actualizados</span>
                              ) : (
                                <span className="text-amber-600">{res.message || 'Sin cambios'}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader><CardTitle>Usuarios ({users.length})</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Nombre</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Fecha</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                        <TableCell>{formatDate(u.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Program Modal */}
      <Dialog open={showProgramModal} onOpenChange={setShowProgramModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingProgram ? 'Editar Programa' : 'Nuevo Programa'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nombre *</label>
              <Input value={programForm.name} onChange={e => setProgramForm({ ...programForm, name: e.target.value })} data-testid="prog-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Universidad *</label>
              <Select value={programForm.university_id} onValueChange={v => setProgramForm({ ...programForm, university_id: v })}>
                <SelectTrigger data-testid="prog-uni-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{universities.map(u => <SelectItem key={u.university_id} value={u.university_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Categoria</label>
                <Input value={programForm.category} onChange={e => setProgramForm({ ...programForm, category: e.target.value })} data-testid="prog-category-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Modalidad</label>
                <Select value={programForm.modality} onValueChange={v => setProgramForm({ ...programForm, modality: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="presencial">Presencial</SelectItem>
                    <SelectItem value="hibrido">Hibrido</SelectItem>
                    <SelectItem value="virtual">Virtual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Precio (MXN) *</label>
                <Input type="number" value={programForm.price} onChange={e => setProgramForm({ ...programForm, price: e.target.value })} data-testid="prog-price-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Duracion (meses)</label>
                <Input type="number" value={programForm.duration_months} onChange={e => setProgramForm({ ...programForm, duration_months: e.target.value })} data-testid="prog-duration-input" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Descripcion</label>
              <textarea className="flex w-full rounded-md border px-3 py-2 text-sm" rows={3} value={programForm.description} onChange={e => setProgramForm({ ...programForm, description: e.target.value })} data-testid="prog-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProgramModal(false)}>Cancelar</Button>
            <Button onClick={saveProgram} disabled={savingProgram || !programForm.name || !programForm.price || !programForm.university_id} className="bg-brand-blue" data-testid="save-program-btn">
              {savingProgram ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* University Modal */}
      <Dialog open={showUniModal} onOpenChange={setShowUniModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingUni ? 'Editar Universidad' : 'Nueva Universidad'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Nombre *</label>
              <Input value={uniForm.name} onChange={e => setUniForm({ ...uniForm, name: e.target.value })} data-testid="uni-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Slug</label>
              <Input value={uniForm.slug} onChange={e => setUniForm({ ...uniForm, slug: e.target.value })} placeholder="ej: mi-universidad" />
            </div>
            <div>
              <label className="text-sm font-medium">Sitio Web</label>
              <Input value={uniForm.website} onChange={e => setUniForm({ ...uniForm, website: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <label className="text-sm font-medium">Descripcion</label>
              <textarea className="flex w-full rounded-md border px-3 py-2 text-sm" rows={3} value={uniForm.description} onChange={e => setUniForm({ ...uniForm, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUniModal(false)}>Cancelar</Button>
            <Button onClick={saveUniversity} disabled={savingUni || !uniForm.name} className="bg-brand-blue" data-testid="save-university-btn">
              {savingUni ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Actualizar Estado de Solicitud</DialogTitle></DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Solicitud: {selectedApp.application_id}</p>
              <div>
                <label className="text-sm font-medium">Nuevo Estado</label>
                <Select value={statusForm.status} onValueChange={v => setStatusForm({ ...statusForm, status: v })}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="submitted">Enviada</SelectItem>
                    <SelectItem value="under_review">En Revision</SelectItem>
                    <SelectItem value="approved">Aprobada</SelectItem>
                    <SelectItem value="rejected">Rechazada</SelectItem>
                    <SelectItem value="documents_required">Docs Pendientes</SelectItem>
                    <SelectItem value="disbursed">Desembolsado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Razon (opcional)</label>
                <Input value={statusForm.reason} onChange={e => setStatusForm({ ...statusForm, reason: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancelar</Button>
            <Button onClick={saveStatus} className="bg-brand-blue" data-testid="save-status-btn">Actualizar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
